import React from 'react';
import VegaChart from '../components/VegaChart';
import WorkflowCards from '../components/WorkflowCards';
import { VEGA_BLOCK_LANGS, WORKFLOW_BLOCK_LANGS } from '../constants';

// Matches bare vegalite/workflow/scenario shorthand lines output by models
// that do not follow proper fenced-code-block formatting instructions.
// e.g. `vegalite type="bar" width=500 ...` rendered as plain text.
const BARE_VIZ_LINE_RE = /^(?:vegalite|workflow|scenario)\s+(?:type|width|height|data|title|tabs)=.*$/gm;

/**
 * Strip bare vegalite/workflow/scenario shorthand lines that a model
 * outputs as plain text instead of a proper fenced code block.
 *
 * @param {string} text - Raw markdown string from the model.
 * @returns {string} Sanitized markdown with malformed viz directives removed.
 */
export function sanitizeMarkdownViz(text) {
  if (!text) return text;
  return text.replace(BARE_VIZ_LINE_RE, '');
}

class VizErrorBoundary extends React.Component {
  state = { caught: false };
  static getDerivedStateFromError() { return { caught: true }; }
  render() {
    if (this.state.caught) return null;
    return this.props.children;
  }
}

function VisualizationPre({ children }) {
  const childArray = React.Children.toArray(children);
  const codeEl = childArray.find(
    (child) => React.isValidElement(child) && child.type === 'code'
  );

  if (!codeEl) return <pre>{children}</pre>;

  const lang = (codeEl.props?.className || '')
    .replace(/^language-/, '')
    .toLowerCase()
    .trim();
  const content = String(codeEl.props?.children || '').replace(/\n$/, '');

  if (VEGA_BLOCK_LANGS.has(lang)) {
    try {
      return (
        <VizErrorBoundary>
          <VegaChart spec={JSON.parse(content)} />
        </VizErrorBoundary>
      );
    } catch {
      return null;
    }
  }

  if (WORKFLOW_BLOCK_LANGS.has(lang)) {
    try {
      return (
        <VizErrorBoundary>
          <WorkflowCards payload={JSON.parse(content)} />
        </VizErrorBoundary>
      );
    } catch {
      return null;
    }
  }

  return <pre>{children}</pre>;
}

const tableComponents = {
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', margin: '16px 0' }}>
      <table style={{
        borderCollapse: 'collapse',
        width: '100%',
        fontSize: '0.9rem',
        lineHeight: 1.5,
      }}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead style={{ backgroundColor: 'var(--bg-secondary, #f5f5f5)' }}>
      {children}
    </thead>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr style={{ borderBottom: '1px solid var(--border-color, #ddd)' }}>
      {children}
    </tr>
  ),
  th: ({ children }) => (
    <th style={{
      padding: '8px 12px',
      textAlign: 'left',
      fontWeight: 600,
      border: '1px solid var(--border-color, #ddd)',
      whiteSpace: 'nowrap',
    }}>
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td style={{
      padding: '8px 12px',
      border: '1px solid var(--border-color, #ddd)',
      verticalAlign: 'top',
    }}>
      {children}
    </td>
  ),
};

export const vizMarkdownComponents = {
  pre: VisualizationPre,
  ...tableComponents,
};

export const noVizMarkdownComponents = {
  code: () => null,
  ...tableComponents,
};
